import { reportApiError } from "api/api-server-actions";

/**
 * Retry function with exponential backoff for network errors
 */
export async function retryOnNetworkError<T>(
  operation: () => Promise<T>,
  operationName: string,
  maxRetries: number = 3,
): Promise<T> {
  const initialMaxRetries = maxRetries;

  async function retryWithBackoff<TInner>(
    op: () => Promise<TInner>,
    name: string,
    remainingRetries: number,
  ): Promise<TInner> {
    try {
      return await op();
    } catch (error) {
      // Check if it's a network error that we should retry
      const isNetworkError =
        error instanceof Error &&
        (error.message.includes("Network Error") ||
          error.message.includes("ERR_NETWORK") ||
          error.message.includes("timeout"));

      if (isNetworkError && remainingRetries > 1) {
        console.warn(
          `${name} failed with network error, retrying... (${remainingRetries - 1} attempts left)`,
        );
        // Wait a bit before retrying (exponential backoff)
        const attemptNumber = initialMaxRetries - remainingRetries + 1;
        await new Promise((resolve) => {
          setTimeout(resolve, 2 ** (attemptNumber - 1) * 1000);
        });
        return retryWithBackoff(op, name, remainingRetries - 1);
      }

      // If it's not a network error or we've exhausted retries
      reportApiError(name, error);
      throw error;
    }
  }

  return retryWithBackoff(operation, operationName, maxRetries);
}
